import {computed, effect, inject, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {patchState, signalStore, withComputed, withHooks, withMethods, withState} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {pipe, switchMap, tap} from 'rxjs';
import {tapResponse} from '@ngrx/operators';
import {FoodItem, Order} from '../core/models';
import {CART_KEY} from '../core/constants';
import {ApiOrder} from '../services/api.order';
import {SessionStore} from './session.store';

export interface CartState {
  cart: FoodItem[];
  isCartLoading: boolean;
}

export const CartStore = signalStore(
  {providedIn: 'root'},
  withState<CartState>({
    cart: [],
    isCartLoading: false
  }),
  withComputed((store) => ({
    cartTotal: computed(() => store.cart().reduce((acc, item) => acc + (parseFloat(item.price) * item.quantity), 0)),
    cartCount: computed(() => store.cart().reduce((acc, item) => acc + item.quantity, 0)),
  })),
  withMethods((
    store,
    apiOrder = inject(ApiOrder),
    sessionStore = inject(SessionStore)
  ) => ({
    addToCart: (product: FoodItem) => {
      if (sessionStore.isOrderLocked()) {
        throw new Error('La orden está bloqueada porque se solicitó la cuenta.');
      }

      const currentCart = store.cart();
      const existingItem = currentCart.find(item => item.productId === product.productId);

      if (existingItem) {
        patchState(store, {
          cart: currentCart.map(item =>
            item.productId === product.productId
              ? {...item, quantity: item.quantity + 1}
              : item
          )
        });
      } else {
        patchState(store, {
          cart: [...currentCart, {...product, quantity: 1, cartLineItemId: Date.now().toString(), notes: ''}]
        });
      }
    },

    removeFromCart: (cartLineItemId: string) => {
      patchState(store, {
        cart: store.cart().filter(item => item.cartLineItemId !== cartLineItemId)
      });
    },

    updateQuantity: (cartLineItemId: string, delta: number) => {
      patchState(store, {
        cart: store.cart().map(item => {
          if (item.cartLineItemId === cartLineItemId) {
            const newQty = Math.max(1, item.quantity + delta);
            return {...item, quantity: newQty};
          }
          return item;
        })
      });
    },

    clearCart: () => patchState(store, {cart: []}),

    updateNotes: (cartLineItemId: string, notes: string) => {
      patchState(store, {
        cart: store.cart().map(item =>
          item.cartLineItemId === cartLineItemId ? {...item, notes} : item
        )
      });
    },

    placeOrder: rxMethod<{ onSuccess: (status: string) => void, onError: (err: any) => void }>(
      pipe(
        tap(() => patchState(store, {isCartLoading: true})),
        switchMap(({onSuccess, onError}) => {
          const orderId = sessionStore.orderRestaurantId();
          const customerId = sessionStore.customerId() || 'guest';

          if (!orderId) {
            patchState(store, {isCartLoading: false});
            onError('No se encontró un ID de orden activo.');
            return [];
          }

          sessionStorage.setItem('current_order_id', orderId);
          sessionStorage.setItem('current_customer_id', customerId);

          const productRequests = store.cart().map(item => ({
            customerName: sessionStore.customerName(),
            orderRestaurantId: orderId,
            customerId: customerId,
            productId: item.productId,
            quantity: item.quantity,
            notes: item.notes || '',
            price: parseFloat(item.price) // Cambiado de parseInt a parseFloat
          }));

          return apiOrder.addProductToOrder(productRequests).pipe(
            tapResponse({
              next: ({status}) => {
                const finishedOrder: Order = {
                  orderId: orderId,
                  items: [...store.cart()],
                  table: sessionStore.selectedTable() || '',
                  total: store.cartTotal(),
                  customerName: sessionStore.customerName(),
                  status: 'pending',
                  createdAt: new Date().toISOString()
                };

                const newActiveOrders = [...sessionStore.activeOrders(), finishedOrder];
                sessionStore.updateActiveOrders(newActiveOrders);

                patchState(store, {isCartLoading: false, cart: []});

                onSuccess(status);
              },
              error: (err) => {
                patchState(store, {isCartLoading: false});
                onError(err);
              }
            })
          );
        })
      )
    )
  })),
  withHooks({
    onInit(store) {
      const platformId = inject(PLATFORM_ID);
      if (isPlatformBrowser(platformId)) {

        const saved = localStorage.getItem(CART_KEY);
        if (saved) {
          try {
            patchState(store, {cart: JSON.parse(saved)});
          } catch (e) {
            console.error('Error parseando el carrito de localStorage', e);
          }
        }

        effect(() => {
          localStorage.setItem(CART_KEY, JSON.stringify(store.cart()));
        });
      }
    },
  })
);
